import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { FramingStrip } from '@/components/FramingStrip'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import { SpaceDetail } from '@/components/SpaceDetail'
import { clusterStatus } from '@/lib/clusterColor'
import { brandMentionsInSpace } from '@/lib/clusters'
import { ratePct } from '@/lib/format'
import { springs } from '@/lib/motion/presets'
import type { Confidence, IntentCluster, PerQueryEntry, Run } from '@/lib/types'

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  high: 'Core',
  medium: 'Adjacent',
  low: 'Speculative',
}

type SortDir = 'best' | 'worst'

const SORT_INDICATOR = 'spaces-sort-indicator'

const SORTS: readonly [SortDir, string][] = [
  ['best', 'Strongest'],
  ['worst', 'Weakest'],
]

type Mentions = { brand: number; total: number }

function shareOf(m: Mentions | undefined): number {
  return m && m.total > 0 ? m.brand / m.total : 0
}

// Sort by the SAME mention-share the bar shows, so ordering agrees with the bars.
function sortClusters(
  clusters: IntentCluster[],
  mentions: Map<string, Mentions>,
  dir: SortDir
): IntentCluster[] {
  const sign = dir === 'best' ? -1 : 1

  return [...clusters].sort((a, b) => {
    const ra = shareOf(mentions.get(a.id))
    const rb = shareOf(mentions.get(b.id))

    if (ra !== rb) return sign * (ra - rb)
    return a.label.localeCompare(b.label)
  })
}

interface RowProps {
  run: Run
  cluster: IntentCluster
  mentions: { brand: number; total: number }
  selected: boolean
  onToggle: () => void
}

// One space as a visibility meter. The bar's fill = the brand's SHARE OF ANSWERS
// (mentions ÷ total answers) — the SAME metric as the "N/M answers" label and the
// Rivals leaderboard, so the bar, the count, and the ranking all agree. (We
// deliberately don't use cluster.scores.rate, which is a different, looser
// "appeared in ≥1 engine per query" figure that disagreed with the counts.)
function SpaceRow({ run, cluster, mentions, selected, onToggle }: RowProps) {
  const rate = mentions.total > 0 ? mentions.brand / mentions.total : 0
  const status = clusterStatus(rate)
  const pct = Math.max(2, Math.round(rate * 100))

  const fill =
    status === 'strong'
      ? 'bg-emerald-500'
      : status === 'partial'
        ? 'bg-amber-500'
        : 'bg-red-500'

  // When selected, the row + its dropdown are wrapped in ONE bordered container
  // so they read as a single connected object (not two floating boxes).
  return (
    <motion.li
      layout="position"
      transition={springs.listReorder}
      className={`overflow-hidden rounded-xl border transition-colors ${
        selected
          ? 'border-primary/50 bg-primary/5'
          : 'border-border bg-card hover:border-ring/60'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={selected}
        className="w-full px-4 py-3 text-left"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{cluster.label}</span>
            <span className="shrink-0 rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {CONFIDENCE_LABEL[cluster.confidence]}
            </span>
          </span>

          <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums">
            <span className="text-base font-semibold">{ratePct(rate)}%</span>
            <span className="text-[11px] text-muted-foreground">
              · {mentions.brand}/{mentions.total} answers
            </span>
          </span>
        </div>

        <div className="relative mt-2.5 h-2.5 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${fill} transition-[width] duration-700`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </button>

      {selected && <SpaceDetail run={run} cluster={cluster} />}
    </motion.li>
  )
}

export function SpacesLens({ run }: { run: Run }) {
  const clusters = run.clusters ?? []
  const [sortDir, setSortDir] = useState<SortDir>('best')

  // Precompute per-space mention counts once (drives both the bars and the sort).
  const mentionsById = useMemo(() => {
    const map = new Map<string, Mentions>()
    const perQuery: PerQueryEntry[] = run.perQuery

    for (const c of clusters) {
      map.set(c.id, brandMentionsInSpace(c.id, perQuery))
    }

    return map
  }, [clusters, run.perQuery])

  const sorted = useMemo(
    () => sortClusters(clusters, mentionsById, sortDir),
    [clusters, mentionsById, sortDir]
  )

  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (clusters.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        No spaces were mapped for this run.
      </p>
    )
  }

  return (
    <div>
      <FramingStrip run={run} variant="solo" />

      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          How visible {run.brand} is across each consumer intent. The bar is
          your share of answers; color flags how weak it is.
        </p>

        {/* Custom sort: strongest-first (default) or weakest-first. */}
        <div className="flex shrink-0 overflow-hidden rounded-lg border border-border text-xs">
          {SORTS.map(([dir, label]) => (
            <button
              key={dir}
              type="button"
              onClick={() => setSortDir(dir)}
              className={`relative cursor-pointer px-3 py-1.5 transition-colors ${
                sortDir === dir
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {sortDir === dir && (
                <SegmentedIndicator layoutId={SORT_INDICATOR} />
              )}
              <span className="relative">{label} first</span>
            </button>
          ))}
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {sorted.map((c) => (
          <SpaceRow
            key={c.id}
            run={run}
            cluster={c}
            mentions={mentionsById.get(c.id) ?? { brand: 0, total: 0 }}
            selected={c.id === selectedId}
            onToggle={() => setSelectedId(c.id === selectedId ? null : c.id)}
          />
        ))}
      </ul>
    </div>
  )
}
